import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  StorefrontOutlined as BusinessIcon,
  PersonOutline as CitizenIcon,
  SchoolOutlined as StudentIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import {
  getPublicSiteBrand,
  showKnlsLms,
  showLoksyApps,
} from '../../utils/publicSiteBrand';

const FONT = '"Space Grotesk", "Trebuchet MS", sans-serif';

const PORTALS = [
  {
    id: 'business',
    to: '/locsydashboard',
    eyebrow: 'Loksy Mitra / Online Centers / Operators',
    title: 'Locsy Dashboard',
    blurb: 'Bills, operators, coupons, and shop tools for admins and franchise teams.',
    who: 'Loksy Mitra · Online Centers · Operators',
    cta: 'Continue to business login',
    icon: BusinessIcon,
    accent: '#0F766E',
    accentSoft: 'rgba(15, 118, 110, 0.12)',
    glow: 'rgba(15, 118, 110, 0.22)',
  },
  {
    id: 'citizen',
    to: '/user-login',
    eyebrow: 'Loksy Citizens',
    title: 'Citizen login',
    blurb: 'Job alerts, coupons, and personal services for everyday Locsy users.',
    who: 'Job seekers · Coupon users · Residents',
    cta: 'Continue to citizen login',
    icon: CitizenIcon,
    accent: '#0369A1',
    accentSoft: 'rgba(3, 105, 161, 0.12)',
    glow: 'rgba(3, 105, 161, 0.22)',
  },
  {
    id: 'student',
    to: '/student/login',
    eyebrow: 'Students',
    title: 'Student LMS',
    blurb: 'Courses, track sheets, quizzes, and learning progress in one place.',
    who: 'Enrolled students · Course learners',
    cta: 'Continue to student login',
    icon: StudentIcon,
    accent: '#B45309',
    accentSoft: 'rgba(180, 83, 9, 0.12)',
    glow: 'rgba(180, 83, 9, 0.2)',
  },
];

const FOOTER_LINKS_LOKSY = [
  { label: 'Privacy', to: '/privacy-policy' },
  { label: 'Terms', to: '/locsy-terms-and-conditions' },
];

const FOOTER_LINKS_KNLS = [
  { label: 'Services', to: '/services' },
  { label: 'Courses', to: '/training-programs' },
  { label: 'Privacy', to: '/privacy-policy' },
  { label: 'Terms', to: '/terms-and-conditions' },
];

function PortalCard({ portal, index }) {
  const Icon = portal.icon;

  return (
    <Box
      component={RouterLink}
      to={portal.to}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: { xs: '20px', md: '24px' },
        bgcolor: 'rgba(255, 255, 255, 0.82)',
        backdropFilter: 'blur(14px)',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
        minHeight: { xs: 220, md: 280 },
        p: { xs: 2.5, md: 3 },
        transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease, border-color 0.35s ease',
        animation: `portalRise 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${0.12 + index * 0.1}s both`,
        boxShadow: `0 18px 40px -28px ${portal.glow}`,
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: portal.accent,
          boxShadow: `0 28px 50px -24px ${portal.glow}`,
          '& .portal-cta': {
            gap: 1.25,
          },
          '& .portal-arrow': {
            transform: 'translateX(4px)',
          },
        },
        '&:focus-visible': {
          outline: `3px solid ${portal.accent}`,
          outlineOffset: 3,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(165deg, ${portal.accentSoft} 0%, transparent 55%)`,
          pointerEvents: 'none',
        }}
      />

      <Stack spacing={2} sx={{ position: 'relative', flex: 1 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '16px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: portal.accentSoft,
            color: portal.accent,
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Box>

        <Box>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: portal.accent,
              mb: 0.75,
            }}
          >
            {portal.eyebrow}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: { xs: '1.35rem', md: '1.5rem' },
              color: '#0F172A',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {portal.title}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: { xs: '0.92rem', md: '0.95rem' },
            color: '#475569',
            lineHeight: 1.55,
            flex: 1,
          }}
        >
          {portal.blurb}
        </Typography>

        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: '0.8rem',
            color: '#64748B',
            pt: 0.5,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
          }}
        >
          For: {portal.who}
        </Typography>

        <Stack
          className="portal-cta"
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            mt: 'auto',
            pt: 1,
            color: portal.accent,
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: '0.95rem',
            transition: 'gap 0.3s ease',
          }}
        >
          <span>{portal.cta}</span>
          <ArrowIcon
            className="portal-arrow"
            sx={{ fontSize: 18, transition: 'transform 0.3s ease' }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * Public entry chooser for loksy.in — maps visitors to the correct login plane.
 */
export default function PortalHome() {
  const isNarrow = useMediaQuery('(max-width:700px)');
  const brand = getPublicSiteBrand();
  const loksyApps = showLoksyApps(brand);
  const lmsApp = showKnlsLms(brand);
  const knlsOnly = brand === 'knls';
  const brandName = knlsOnly ? 'KNLS' : 'LOCSY';
  const portals = PORTALS.filter((portal) => {
    if (portal.id === 'student') return lmsApp;
    return loksyApps;
  }).map((portal) => {
    if (knlsOnly && portal.id === 'student') {
      return {
        ...portal,
        eyebrow: 'KNLS Students',
        title: 'KNLS LMS',
        cta: 'Continue to KNLS LMS',
      };
    }
    return portal;
  });
  const footerLinks = knlsOnly
    ? FOOTER_LINKS_KNLS
    : loksyApps && !lmsApp
      ? FOOTER_LINKS_LOKSY
      : [...FOOTER_LINKS_KNLS.slice(0, 2), ...FOOTER_LINKS_LOKSY];

  useEffect(() => {
    const previous = document.title;
    document.title = knlsOnly
      ? 'KNLS LMS — Student login'
      : 'LOCSY — Choose your portal';
    return () => {
      document.title = previous;
    };
  }, [knlsOnly]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: FONT,
        color: '#0F172A',
        background: `
          radial-gradient(ellipse 90% 60% at 10% -10%, rgba(15, 118, 110, 0.18), transparent 55%),
          radial-gradient(ellipse 70% 50% at 95% 5%, rgba(3, 105, 161, 0.16), transparent 50%),
          radial-gradient(ellipse 60% 40% at 70% 100%, rgba(180, 83, 9, 0.1), transparent 45%),
          linear-gradient(180deg, #F4F7F8 0%, #EEF2F4 45%, #E8EEF0 100%)
        `,
        '@keyframes portalRise': {
          from: { opacity: 0, transform: 'translateY(22px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes brandIn': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* Soft grid atmosphere */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.35,
          backgroundImage: `
            linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, #000 20%, transparent 75%)',
          pointerEvents: 'none',
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: { xs: 4, sm: 5, md: 7 },
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Hero */}
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 720,
            mx: 'auto',
            mb: { xs: 3.5, md: 5 },
            animation: 'brandIn 0.65s cubic-bezier(0.22, 1, 0.36, 1) both',
          }}
        >
          <Typography
            component="p"
            sx={{
              fontFamily: FONT,
              fontWeight: 800,
              fontSize: { xs: '2.75rem', sm: '3.75rem', md: '4.5rem' },
              letterSpacing: { xs: '-0.04em', md: '-0.05em' },
              lineHeight: 0.95,
              mb: 1.5,
              background: 'linear-gradient(120deg, #0F766E 0%, #0C4A6E 55%, #B45309 130%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {brandName}
          </Typography>

          <Typography
            component="h1"
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: { xs: '1.35rem', sm: '1.65rem', md: '1.85rem' },
              letterSpacing: '-0.025em',
              color: '#0F172A',
              mb: 1.25,
              lineHeight: 1.25,
            }}
          >
            {knlsOnly ? 'Student LMS login' : 'Pick your portal to sign in'}
          </Typography>

          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              color: '#64748B',
              lineHeight: 1.6,
              maxWidth: 520,
              mx: 'auto',
            }}
          >
            {knlsOnly
              ? 'Open the KNLS student LMS for courses, track sheets, quizzes, and progress.'
              : loksyApps && !lmsApp
                ? 'One place for Mitra shops and Loksy citizens — choose the card that matches who you are.'
                : 'One place for business teams, citizens, and students — choose the card that matches who you are.'}
          </Typography>
        </Box>

        {/* Portal cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: portals.length === 1 ? 'minmax(0, 420px)' : `repeat(${Math.min(portals.length, 3)}, 1fr)`,
            },
            justifyContent: 'center',
            gap: { xs: 2, md: 2.5 },
            width: '100%',
            maxWidth: 1100,
            mx: 'auto',
            flex: 1,
            alignContent: 'start',
          }}
        >
          {portals.map((portal, index) => (
            <PortalCard key={portal.id} portal={portal} index={index} />
          ))}
        </Box>

        {/* Helper strip */}
        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            maxWidth: 720,
            mx: 'auto',
            textAlign: 'center',
            animation: 'brandIn 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both',
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: '0.85rem',
              color: '#64748B',
              lineHeight: 1.65,
            }}
          >
            {knlsOnly
              ? 'Students use this portal to open courses, track sheets, and the LMS classroom.'
              : loksyApps && !lmsApp
                ? isNarrow
                  ? 'Not sure? Mitra runs the shop. Citizens use jobs, coupons and local services.'
                  : 'Not sure which one? Mitra is for shops and operators. Citizens use jobs, coupons and local services.'
                : isNarrow
                  ? 'Not sure? Business runs the shop, citizens use jobs & coupons, students open LMS.'
                  : 'Not sure which one? Business is for shops and institutes. Citizens use job alerts and coupons. Students open the LMS.'}
          </Typography>

          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            spacing={1}
            useFlexGap
            sx={{ mt: 2.5, gap: 1 }}
          >
            {footerLinks.map((item) => (
              <Button
                key={item.to}
                component={RouterLink}
                to={item.to}
                size="small"
                sx={{
                  fontFamily: FONT,
                  textTransform: 'none',
                  color: '#475569',
                  fontWeight: 600,
                  borderRadius: '10px',
                  px: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(15, 23, 42, 0.05)',
                    color: '#0F766E',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Typography
            sx={{
              mt: 3,
              fontFamily: FONT,
              fontSize: '0.75rem',
              color: '#94A3B8',
            }}
          >
            © {new Date().getFullYear()} {knlsOnly ? 'KNLS' : 'Locsy'} ·{' '}
            <Link
              href="https://knls.in"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: 'inherit', fontWeight: 600 }}
            >
              knls.in
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
